import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { USER_ROLES_BY_NAME } from 'src/constants/userSetting';
/**
 * Nav and actions live in the palette alongside entity results, so it doubles as "how do I get to
 * Reporting" without ever running a query. That is what makes it a command palette rather than a
 * search box.
 */
// Every path here must exist in src/configs/routes.js. Four of them did not, so selecting
// Properties, Residents, Utility Bills or Settings fell through to the error page: the palette
// looked broken while it was faithfully navigating to routes nobody had registered.
export const COMMANDS = [{
  id: 'dashboard',
  label: 'Dashboard',
  section: 'Navigation',
  path: '/active_dashboard',
  keywords: 'home overview'
}, {
  id: 'properties',
  label: 'Properties',
  section: 'Navigation',
  path: '/new_properties',
  keywords: 'buildings units'
}, {
  id: 'residents',
  label: 'Residents',
  section: 'Navigation',
  path: '/residencies',
  keywords: 'tenants people'
}, {
  id: 'bills',
  label: 'Utility Bills',
  section: 'Navigation',
  path: '/bills',
  keywords: 'invoices utilities'
}, {
  id: 'allocations',
  label: 'Allocations',
  section: 'Navigation',
  path: '/allocations',
  keywords: 'charges history'
}, {
  id: 'reporting',
  label: 'Reporting',
  section: 'Navigation',
  path: '/reporting',
  keywords: 'reimbursement recovery export'
}, {
  id: 'payments',
  label: 'Payments',
  section: 'Navigation',
  path: '/payments',
  keywords: 'transactions ledger'
}, {
  id: 'settings',
  label: 'Settings',
  section: 'Navigation',
  path: '/profile-settings',
  keywords: 'profile team account'
}, {
  id: 'admin',
  label: 'Admin',
  section: 'Navigation',
  path: '/admin',
  keywords: 'companies users',
  adminOnly: true
}, {
  id: 'livy',
  label: 'Ask Livy',
  section: 'Actions',
  path: '/livy',
  keywords: 'assistant ai chat help'
}];
const ADMIN_ROLES = [USER_ROLES_BY_NAME.LivableAdmin, USER_ROLES_BY_NAME.LivableSuperAdmin];
export const isAdminRole = role => ADMIN_ROLES.includes(String(role));

/**
 * Under two characters the palette is pure navigation, no query is run at all. Matching is on
 * label and keywords so "recovery" finds Reporting.
 */
export function filterCommands(query, role) {
  const visible = COMMANDS.filter(c => !c.adminOnly || isAdminRole(role));
  const q = query.trim().toLowerCase();
  if (!q) return visible;
  return visible.filter(c => {
    var _c$keywords$includes, _c$keywords;
    return c.label.toLowerCase().includes(q) || c.section.toLowerCase().includes(q) || ((_c$keywords$includes = (_c$keywords = c.keywords) === null || _c$keywords === void 0 ? void 0 : _c$keywords.includes(q)) !== null && _c$keywords$includes !== void 0 ? _c$keywords$includes : false);
  });
}
export const SEARCH_MIN_CHARS = 2;
export const shouldSearch = query => query.trim().length >= SEARCH_MIN_CHARS;

/** Deduped so the same record arriving from two sources renders once. */
export function dedupe(items) {
  const seen = new Set();
  return items.filter(i => {
    var _i$subtitle;
    const key = "".concat(i.type, ":").concat(i.title, "|").concat((_i$subtitle = i.subtitle) !== null && _i$subtitle !== void 0 ? _i$subtitle : '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** One entity row from search_everything, rendered in the same list as navigation. */

/**
 * Entity results open Livy asking about the record, rather than a deep link. This app has no
 * route that takes a property or resident id, selecting one navigated to the error page, and a
 * result that goes nowhere is worse than no result at all.
 */
const singular = group => {
  if (group.endsWith('ies')) return "".concat(group.slice(0, -3), "y"); // properties -> property, not propertie
  return group.endsWith('s') ? group.slice(0, -1) : group;
};

/** Kept as a secondary action on a row: "ask Livy about this" is never what a click does. */
export const askPath = hit => "/livy?ask=".concat(encodeURIComponent("Tell me about the ".concat(singular(hit.type), " \"").concat(hit.title, "\".")));

// Each endpoint names its fields differently: the property resolver answers propertyAddress and
// propertyId, the company list companyName and companyId. Read by any of the names in use.
const TITLE_FIELDS = ['name', 'propertyAddress', 'companyName', 'title', 'displayName', 'propertyName', 'fullName', 'unitName', 'label'];
const SUBTITLE_FIELDS = ['address', 'addressLine1', 'code', 'email', 'propertyName', 'propertyStatus', 'status'];
const firstString = (row, fields) => {
  for (const f of fields) {
    const v = row[f];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
};

/**
 * search_everything answers as { results: { properties: [...], residents: [...] } }, with each row
 * being the entity's own shape rather than a search-specific one. Flattened here, and labelled from
 * whichever name-ish field the entity actually has, so a new group needs no front-end change to
 * appear.
 */
/**
 * Knowledge-base hits render beside entity hits, because "how do I close a cycle" and "where is
 * Maple Court" are the same keystroke to the person typing.
 */
export function toKnowledgeCommands() {
  let articles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return articles.slice(0, 5).map(a => ({
    id: "kb-".concat(a.slug),
    label: a.title,
    section: 'Help',
    path: "/livy/kb?article=".concat(encodeURIComponent(a.slug)),
    keywords: 'knowledge base help article'
  }));
}
export function flattenSearchResults(results) {
  if (!results || typeof results !== 'object' || Array.isArray(results)) return [];
  return Object.entries(results).flatMap(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      group = _ref2[0],
      rows = _ref2[1];
    if (!Array.isArray(rows)) return [];
    return rows.flatMap(raw => {
      var _firstString;
      if (!raw || typeof raw !== 'object') return [];
      const row = raw;
      // An id is not a title. Showing a bare GUID as a result looks like a bug to the person
      // reading it, so a row with no human-readable name is dropped.
      const title = firstString(row, TITLE_FIELDS);
      if (!title) return [];
      // A result opens the record. The index stores the destination it resolved when the row was
      // written; without one there is nowhere to go, and a result that cannot be opened is worse
      // than no result, so it is dropped rather than turned into a question for the assistant.
      const href = typeof row.href === 'string' ? row.href : undefined;
      if (!href) return [];
      const hit = {
        type: group,
        title,
        subtitle: (_firstString = firstString(row, SUBTITLE_FIELDS)) !== null && _firstString !== void 0 ? _firstString : group
      };
      return [_objectSpread(_objectSpread({}, hit), {}, {
        path: href
      })];
    });
  });
}

/**
 * Entity results are appended to the navigation matches rather than shown in a second pane, so
 * ArrowDown walks one list and Enter always means "open the highlighted thing".
 */
export function toCommands(hits) {
  return dedupe(hits).map((h, i) => ({
    id: "hit-".concat(i, "-").concat(h.type, "-").concat(h.title),
    label: h.title,
    section: 'Results',
    path: h.path,
    keywords: h.subtitle
  }));
}

/**
 * One ranked list, rather than navigation then entities then articles in fixed blocks.
 *
 * Grouping by source meant a weak navigation match outranked an exact property name simply because
 * navigation was rendered first, which reads as the search ignoring what was typed.
 */
export function rankItems(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const score = c => {
    var _c$keywords2;
    const label = c.label.toLowerCase();
    if (label === q) return 100;
    if (label.startsWith(q)) return 80;
    if (label.includes(q)) return 60;
    if (((_c$keywords2 = c.keywords) !== null && _c$keywords2 !== void 0 ? _c$keywords2 : '').toLowerCase().includes(q)) return 30;
    return 10;
  };

  // Stable within a score, so equal matches keep the order their source produced.
  return items.map((c, i) => ({
    c,
    i,
    s: score(c)
  })).sort((a, b) => b.s - a.s || a.i - b.i).map(_ref3 => {
    let c = _ref3.c;
    return c;
  });
}